import { useState, useEffect } from 'react';
import '@/App.css';
import './styles/modern-theme.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Motorcycles from './pages/Motorcycles';
import RecoveryVehicle from './pages/RecoveryVehicle';
import MotorcycleHistory from './pages/MotorcycleHistory';
import Batteries from './pages/Batteries';
import Riders from './pages/Riders';
import Lessees from './pages/Lessees';
import Users from './pages/Users';
import Maintenance from './pages/Maintenance';
import Fines from './pages/Fines';
import Inbox from './pages/Inbox';
import Salik from './pages/Salik';
import Spares from './pages/Spares';
import Insurance from './pages/Insurance';
import Invoices from './pages/Invoices';
import Accounts from './pages/Accounts';
import Reports from './pages/Reports';
import BatterySwaps from './pages/BatterySwaps';
import BatteryImport from './pages/BatteryImport';
import CriticalAlerts from './pages/CriticalAlerts';
import Profile from './pages/Profile';
import Integrations from './pages/Integrations';
import WhatsApp from './pages/WhatsApp';
import SearchableDropdownDemo from './pages/SearchableDropdownDemo';
import Approvals from './pages/Approvals';
import Settings from './pages/Settings';
import Talent from './pages/Talent';
import Warnings from './pages/Warnings';
import TAP from './pages/TAP';
import Hiring from './pages/Hiring';
import Inventory from './pages/Inventory';
import PdfViewer from './pages/PdfViewer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Analysis from './pages/Analysis';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import InstallPrompt from './components/InstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletedAlertShown, setDeletedAlertShown] = useState(false);

  // Global axios interceptor to handle deleted users
  useEffect(() => {
    // Request interceptor - show loading cursor for mutating requests
    let activeRequests = 0;
    const reqInterceptor = axios.interceptors.request.use((config) => {
      const method = (config.method || '').toLowerCase();
      if (['post', 'put', 'delete', 'patch'].includes(method)) {
        activeRequests++;
        document.body.classList.add('loading-cursor');
      }
      return config;
    });
    
    const interceptor = axios.interceptors.response.use(
      (response) => {
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0) document.body.classList.remove('loading-cursor');
        return response;
      },
      (error) => {
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0) document.body.classList.remove('loading-cursor');
        
        // Don't handle 401 from login endpoint - let the login page handle it
        const isLoginRequest = error.config?.url?.includes('/auth/login');
        
        if (error.response?.status === 401 && !isLoginRequest) {
          // User token is invalid or user was deleted
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setUser(null);
          
          // Show alert if user was deleted (only once)
          if (error.response?.data?.detail?.includes('deleted') && !deletedAlertShown) {
            setDeletedAlertShown(true);
            alert('⚠️ SECURITY ALERT: Your account has been deleted by an administrator. You have been logged out immediately.');
          }
          
          // Redirect to login
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(interceptor);
    };
  }, [deletedAlertShown]);

  // CRITICAL: Active session validation - checks every 3 seconds if user is still valid
  useEffect(() => {
    let intervalId;
    
    if (isAuthenticated && user) {
      // Heartbeat: Check if user account is still valid and refresh permissions every 10 seconds
      intervalId = setInterval(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          clearInterval(intervalId);
          return;
        }

        try {
          // Fetch latest user data including permissions
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update user state with latest data (including permissions)
          // Only update if permissions have changed to avoid unnecessary re-renders
          const newUserData = response.data;
          if (JSON.stringify(newUserData.permissions) !== JSON.stringify(user.permissions)) {
            console.log('Permissions updated, refreshing user data...');
            setUser(newUserData);
          }
        } catch (error) {
          if (error.response?.status === 401) {
            // User has been deleted or token is invalid
            clearInterval(intervalId);
            localStorage.removeItem('token');
            setIsAuthenticated(false);
            setUser(null);
            
            // Only show alert once and if it's a deletion
            if (error.response?.data?.detail?.includes('deleted') && !deletedAlertShown) {
              setDeletedAlertShown(true);
              alert('⚠️ SECURITY ALERT: Your account has been deleted by an administrator. You are being logged out immediately.');
            }
            window.location.href = '/login';
          }
        }
      }, 10000); // Check every 10 seconds (was 3 seconds, increased for better performance)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAuthenticated, user, deletedAlertShown]);

  // Initial auth check on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000
          });
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const handleLogin = (token, userData) => {
    // Clear any previous deletion alert state when user logs in successfully
    setDeletedAlertShown(false);
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Helper to get first permitted page for login redirect
  const getFirstPermittedPage = () => {
    if (!user) return '/';
    
    // Admin/Super Admin always go to dashboard
    if (user.role === 'Super Admin' || user.role === 'Admin') {
      return '/';
    }

    // Check permissions for other users
    const moduleToPath = {
      dashboard: '/',
      motorcycles: '/motorcycles',
      batteries: '/batteries',
      batterySwaps: '/battery-swaps',
      riders: '/riders',
      lessees: '/lessees',
      maintenance: '/maintenance',
      fines: '/fines',
      inbox: '/inbox',
      salik: '/salik',
      talent: '/talent',
      tap: '/tap',
      spares: '/spares',
      insurance: '/insurance',
      invoices: '/invoices',
      reports: '/reports'
    };

    if (user.permissions) {
      for (const [module, path] of Object.entries(moduleToPath)) {
        if (user.permissions[module]?.view === true) {
          return path;
        }
      }
    }

    return '/profile'; // Fallback
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-xl font-medium text-slate-700">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <InstallPrompt />
      <ErrorBoundary>
        <BrowserRouter>
        <Routes>
          {/* Public: Privacy Policy (required by Meta/WhatsApp compliance) */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          {/* Public: Terms & Conditions (required by Meta/WhatsApp compliance) */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to={getFirstPermittedPage()} replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          {/* PDF Viewer - standalone route without layout */}
          <Route
            path="/pdf-viewer"
            element={
              isAuthenticated ? (
                <PdfViewer />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            {/* Dashboard - protected */}
            <Route index element={
              <ProtectedRoute user={user} moduleKey="dashboard">
                <Dashboard user={user} />
              </ProtectedRoute>
            } />
            
            {/* Profile - always accessible */}
            <Route path="profile" element={<Profile />} />
            
            {/* Protected routes */}
            <Route path="motorcycles" element={
              <ProtectedRoute user={user} moduleKey="motorcycles">
                <Motorcycles user={user} />
              </ProtectedRoute>
            } />
            <Route path="motorcycles/:id/history" element={
              <ProtectedRoute user={user} moduleKey="motorcycles">
                <MotorcycleHistory />
              </ProtectedRoute>
            } />
            <Route path="motorcycles/recovery" element={
              <ProtectedRoute user={user} moduleKey="motorcycles">
                <RecoveryVehicle />
              </ProtectedRoute>
            } />
            <Route path="batteries" element={
              <ProtectedRoute user={user} moduleKey="batteries">
                <Batteries />
              </ProtectedRoute>
            } />
            <Route path="riders" element={
              <ProtectedRoute user={user} moduleKey="riders">
                <Riders user={user} />
              </ProtectedRoute>
            } />
            <Route path="lessees" element={
              <ProtectedRoute user={user} moduleKey="lessees">
                <Lessees user={user} />
              </ProtectedRoute>
            } />
            <Route path="users" element={
              <ProtectedRoute user={user} moduleKey="users">
                <Users currentUser={user} />
              </ProtectedRoute>
            } />
            <Route path="maintenance" element={
              <ProtectedRoute user={user} moduleKey="maintenance">
                <Maintenance user={user} />
              </ProtectedRoute>
            } />
            <Route path="fines" element={
              <ProtectedRoute user={user} moduleKey="fines">
                <Fines user={user} />
              </ProtectedRoute>
            } />
            <Route path="inbox" element={
              <ProtectedRoute user={user} moduleKey="inbox">
                <Inbox user={user} />
              </ProtectedRoute>
            } />
            <Route path="approvals" element={
              <ProtectedRoute user={user} moduleKey="approvals">
                <Approvals user={user} />
              </ProtectedRoute>
            } />
            <Route path="salik" element={
              <ProtectedRoute user={user} moduleKey="salik">
                <Salik user={user} />
              </ProtectedRoute>
            } />
            <Route path="talent" element={
              <ProtectedRoute user={user} moduleKey="talent">
                <Talent user={user} />
              </ProtectedRoute>
            } />
            <Route path="talent/warnings" element={
              <ProtectedRoute user={user} moduleKey="talent">
                <Warnings user={user} />
              </ProtectedRoute>
            } />
            <Route path="tap" element={
              <ProtectedRoute user={user} moduleKey="tap">
                <TAP user={user} />
              </ProtectedRoute>
            } />
            <Route path="hiring" element={
              <ProtectedRoute user={user} moduleKey="hiring">
                <Hiring user={user} />
              </ProtectedRoute>
            } />
            <Route path="spares" element={
              <ProtectedRoute user={user} moduleKey="spares">
                <Spares user={user} />
              </ProtectedRoute>
            } />
            <Route path="inventory" element={
              <ProtectedRoute user={user} moduleKey="inventory">
                <Inventory user={user} />
              </ProtectedRoute>
            } />
            <Route path="insurance" element={
              <ProtectedRoute user={user} moduleKey="insurance">
                <Insurance />
              </ProtectedRoute>
            } />
            <Route path="invoices" element={
              <ProtectedRoute user={user} moduleKey="invoices">
                <Accounts user={user} />
              </ProtectedRoute>
            } />
            <Route path="reports" element={
              <ProtectedRoute user={user} moduleKey="reports">
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="battery-swaps" element={
              <ProtectedRoute user={user} moduleKey="batterySwaps">
                <BatterySwaps user={user} />
              </ProtectedRoute>
            } />
            <Route path="battery-import" element={
              <ProtectedRoute user={user} moduleKey="batteries">
                <BatteryImport user={user} />
              </ProtectedRoute>
            } />
            <Route path="critical-alerts" element={
              <ProtectedRoute user={user} moduleKey="dashboard">
                <CriticalAlerts user={user} />
              </ProtectedRoute>
            } />
            <Route path="integrations" element={
              <ProtectedRoute user={user} moduleKey="adminTools" subPage="integrations">
                <Integrations />
              </ProtectedRoute>
            } />
            <Route path="whatsapp" element={
              <ProtectedRoute user={user} moduleKey="adminTools" subPage="whatsapp">
                <WhatsApp user={user} />
              </ProtectedRoute>
            } />
            <Route path="searchable-dropdown-demo" element={
              <ProtectedRoute user={user} moduleKey="search">
                <SearchableDropdownDemo />
              </ProtectedRoute>
            } />
            <Route path="settings" element={
              <ProtectedRoute user={user} moduleKey="users" alsoAllow={['adminTools', 'settings']}>
                <Settings user={user} />
              </ProtectedRoute>
            } />
            <Route path="analysis" element={
              <ProtectedRoute user={user} moduleKey="analysis">
                <Analysis user={user} />
              </ProtectedRoute>
            } />
          </Route>
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;